<template>
  <div>
  <b-col xs="12" lg="12" v-if="model !== null">
        <b-tabs card pills v-model="tabIndex[1]" lazy>
          <b-tab href="#general" @click="tabChange('general')">
            <template slot="title">
              <i class="icon-screen-desktop"></i> {{ $t('general') }}
            </template>
            
            <!-- Project Information Section -->
            <div class="settings-section">
              <h5 class="section-title">{{ $t('settings.projectInformation') }}</h5>
              
              <b-form-group :label="$t('uuid')" label-for="basicName" :label-cols="2" :horizontal="true">
                <b-form-input id="uuid" type="text" :value="model.uuid" disabled></b-form-input>
              </b-form-group>

              <b-form-group :label="$t('name')" label-for="basicName" :label-cols="2" :horizontal="true">
                <b-form-input id="name" type="text" v-model="model.name" :disabled="!$isAdmin()"></b-form-input>
              </b-form-group>

              <b-form-group :label="$t('createdAt')" label-for="basicName" :label-cols="2" :horizontal="true">
                <b-form-input id="name" type="text" :value="$toUserTimeZone(model.CreatedAt)" disabled></b-form-input>
              </b-form-group>
            </div>

            <!-- Payment Preferences Section -->
            <div class="settings-section" v-if="$isAdmin()">
              <h5 class="section-title">{{ $t('settings.paymentPreferences') }}</h5>
              <p class="section-description">{{ $t('settings.paymentPreferencesDescription') }}</p>
              
              <div v-if="noPaymentMethodsAvailable" class="alert alert-warning mt-2 mb-3">
                <i class="fa fa-exclamation-triangle mr-2"></i>
                {{ $t('countryNotSupported') }}
              </div>

              <b-form-group
                v-else
                :label="$t('settings.primaryPaymentMethod')"
                label-for="paymentMethod"
                :label-cols="2"
                :horizontal="true"
              >
                <div class="payment-method-options">
                  <div
                    v-if="cardPaymentsEnabled"
                    class="payment-method-option"
                    :class="{ 'selected': primaryPaymentMethod === 'card' }"
                    @click="primaryPaymentMethod = 'card'"
                  >
                    <div class="option-icon">
                      <b-icon icon="credit-card" font-scale="1.5"></b-icon>
                    </div>
                    <div class="option-content">
                      <span class="option-title">{{ $t('settings.creditCard') }}</span>
                      <span class="option-description">{{ $t('settings.creditCardDefault') }}</span>
                    </div>
                    <div v-if="primaryPaymentMethod === 'card'" class="option-check">
                      <b-icon icon="check-circle-fill"></b-icon>
                    </div>
                  </div>
                  <div
                    v-if="allowBankPayment"
                    class="payment-method-option"
                    :class="{ 'selected': primaryPaymentMethod === 'bankaccount' }"
                    @click="primaryPaymentMethod = 'bankaccount'"
                  >
                    <div class="option-icon">
                      <b-icon icon="bank" font-scale="1.5"></b-icon>
                    </div>
                    <div class="option-content">
                      <span class="option-title">{{ $t('settings.bankAccount') }}</span>
                      <span class="option-description">{{ $t('settings.bankAccountDefault') }}</span>
                    </div>
                    <div v-if="primaryPaymentMethod === 'bankaccount'" class="option-check">
                      <b-icon icon="check-circle-fill"></b-icon>
                    </div>
                  </div>
                </div>
              </b-form-group>
            </div>

            <!-- Save Button -->
            <div class="settings-section" v-if="$isAdmin()">
              <b-form-group :label-cols="2" :horizontal="true">
                <b-button variant="success" @click="saveSettings" class="mr-2">{{ $t('save') }}</b-button>
                <b-button variant="outline-primary" @click="showCreateProjectForm = true" v-if="isOwner()">
                  <i class="fa fa-plus"></i> {{ $t('createNewProject') }}
                </b-button>
              </b-form-group>
            </div>

            <!-- Danger Zone Section -->
            <div class="settings-section danger-zone">
              <h5 class="section-title text-danger">{{ $t('settings.dangerZone') }}</h5>

              <b-form-group :label-cols="2" :horizontal="true">
                <div class="form-action">
                  <b-button variant="danger" v-if="isOwner()" @click="remove">{{ $t('remove') }}</b-button>
                  <b-button variant="primary" @click="leave" v-if="!isOwner()">{{ $t('leave') }}</b-button>
                </div>
              </b-form-group>
            </div>

          </b-tab>
          <b-tab href="#account" @click="tabChange('account')" v-if="isOwner()">
            <template slot="title">
              <i class="fa fa-user"></i> {{ $t('account') }}
            </template>

            <b-tabs v-model="tabIndex[2]">
              <b-tab :title="$t('general')">

                <b-form-group :label="$t('account')" label-for="basicName" :label-cols="2" :horizontal="true">
                  <b-form-input id="id" type="text" :value="model.account.name" disabled></b-form-input>
                </b-form-group>

                <b-card>
                  <AccountInfo :account="model.account" :prices="prices" :currency="currency" v-if="prices" />
                  <b-skeleton height="100" animation="throb" v-else />
                </b-card>

                <!-- Available Discounts Section -->
                <b-card v-if="model.account.discounts && model.account.discounts.length > 0" class="mt-3">
                  <h5 class="mb-3">{{ $t('accountInfo.availableDiscounts') }}</h5>
                  <div v-for="(discount, index) in model.account.discounts" :key="index" class="discount-item mb-3">
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="d-flex align-items-center">
                        <b-badge variant="success" class="mr-2" style="font-size: 1rem; padding: 0.5rem 0.75rem;">
                          <span v-if="discount.type === 'percent'">-{{ discount.amount }}%</span>
                          <span v-else-if="discount.type === 'fixed'">-{{ formatDiscountAmount(discount.amount) }}</span>
                        </b-badge>
                        <div>
                          <strong>{{ discount.name }}</strong>
                          <div class="text-muted" style="font-size: 0.85rem;">
                            <span v-if="!discount.productIds || discount.productIds.length === 0">
                              {{ $t('accountInfo.appliedToAllProducts') }}
                            </span>
                            <span v-else>
                              {{ $t('accountInfo.appliedToSpecificProducts', { count: discount.productIds.length }) }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <b-icon icon="tag-fill" font-scale="1.5" class="text-success"></b-icon>
                    </div>
                  </div>
                </b-card>

                <b-skeleton v-if="!accounts" height="150" animation="throb"></b-skeleton>
                <b-row v-else>
                  <template v-if="availableUpgrades.length > 0">
                    <b-col cols="12">
                    <h4>{{ $t('unlockMoreToday') }}</h4>
                    </b-col>
                    <b-col cols="12" md="6" sm="6" v-for="upgrade in availableUpgrades" :key="upgrade.uuid">
                      <b-card>
                        <h4>{{ upgrade.name }}</h4>
                        <div class="mb-3"><b-form-checkbox switch v-model="accountAnnual"> {{ $t('annually') }}
                          </b-form-checkbox></div>

                        <h3 v-if="accountAnnual">{{ getPriceAndCurrency(upgrade.priceAnnualy()).price }} {{
                          getPriceAndCurrency(upgrade.priceAnnualy()).currency }} <small>{{ $t('perMonthAnnually')
                            }}</small></h3>
                        <h3 v-else>{{ getPriceAndCurrency(upgrade.priceMonthly()).price }} {{
                          getPriceAndCurrency(upgrade.priceMonthly()).currency }} <small>{{ $t('perMonth')
                            }}</small> </h3>

                        <AccountInfo :account="upgrade" :prices="prices" :currency="currency" :key="upgrade.type" />

                        <b-row cols="2" class="mt-4">
                          <b-col><a style="text-decoration:underline" href="mailto:support@protokol.io">{{
                              $t('requestTrial') }}</a></b-col>
                          <b-col><protokol-checkout-button productType="account" :reference="upgrade.uuid"
                              :options="{ variant: accountAnnual ? 'annual' : 'month' }">{{ $t('upgrade')
                              }}</protokol-checkout-button></b-col>
                        </b-row>
                      </b-card>
                    </b-col>
                  </template>
                </b-row>

              </b-tab>
              <b-tab :title="$t('subscriptions')">
                <Subscription :settings="model.settings" @subscribed="subscribed" />
              </b-tab>
              <b-tab :title="$t('invoices.title')">
                <Invoices />
              </b-tab>
              <b-tab :title="$t('savedCards.tabTitle')">
                <SavedCards />
              </b-tab>
            </b-tabs>
          </b-tab>

          <!-- <b-tab href="#billing" @click="tabChange('billing')" v-if="isOwner()">
            <template slot="title">
              <i class="fa fa-money"></i>{{ $t('billing') }}
            </template>

            <Billing />
          </b-tab> -->

          <b-tab href="#usage" @click="tabChange('usage')" v-if="isOwner() && model.account && model.account.pay_as_you_go">
            <template slot="title">
              <i class="fa fa-database"></i> {{ $t('usage') }}
            </template>

            <BillingUsage />
          </b-tab>

          <b-tab href="#billing-modes" @click="tabChange('billing-modes')" v-if="isOwner()">
            <template slot="title">
              <i class="fa fa-sliders"></i> {{ $t('settings.billingModes.title') }}
            </template>

            <BillingModes />
          </b-tab>
        </b-tabs>
  </b-col>

  <main-dialog
    :show="showCreateProjectForm"
    :title="$t('createNewProject')"
    @close="showCreateProjectForm = false; createProjectError = null"
  >
    <b-form @submit.prevent="createProject">
      <b-alert v-if="createProjectError" variant="danger" show class="mb-3">{{ createProjectError }}</b-alert>
      <b-form-group :label="$t('projectNameLabel')">
        <b-form-input
          v-model="newProjectName"
          :placeholder="$t('projectNameLabel')"
          required
        />
      </b-form-group>
      <div class="text-right">
        <b-button variant="secondary" class="mr-2" @click="showCreateProjectForm = false; createProjectError = null">{{ $t('cancel') }}</b-button>
        <b-button type="submit" variant="primary" :disabled="creatingProject">
          <b-spinner small v-if="creatingProject" />
          <span v-else>{{ $t('createProjectBtn') }}</span>
        </b-button>
      </div>
    </b-form>
  </main-dialog>
  </div>
</template>

<script>
import Input from "@/components/form/Input"
import Select from "@/components/form/Select"
import Billing from "./Settings/Billing"
import Subscription from "./Settings/Subscription"
import SavedCards from "./Settings/SavedCards"
import Invoices from "./Invoices"
import BillingUsage from "./BillingUsage"
import BillingModes from "./Settings/BillingModes"
import AccountInfo from './AccountInfo'
import Account from '@/lib/Account'
import Checkout from '@/lib/Checkout'
import BillingInfo from '@/views/users/BillingInfo'
import Price from '@/plugins/protokol/lib/Price'
import Env from '@/plugins/protokol/lib/Env'

export default {
  name: "Settings",
  components: {
    InputField: Input,
    SelectField: Select,
    Billing,
    Subscription,
    SavedCards,
    Invoices,
    BillingUsage,
    BillingModes,
    AccountInfo,
    BillingInfo
  },
  data: function () {
    return {
      info: null,
      revokedSecrets: [],
      tabIndex: [0, 0],
      transferModal: {
        show: false,
        user_uuid: ""
      },
      paymentMethods: [
        {
          name: "Invoice",
          provider: "protokol",
          method: "invoice"
        }
      ],
      tabHash: ["#general", "#account", "#usage", "#credentials", "#billing-modes"],
      reserveRequestsModal: false,
      reservedRequestsAmount: 0,
      reservedRequestsQty: 10000,
      calculatingPrice: false,
      maxReservedRequests: 0,
      canReserveRequests: false,
      prices: null,
      accounts: null,
      showBillingInformationModal: false,
      billingInfoSaved: false,
      accountAnnual: true,
      primaryPaymentMethod: 'card', // 'card' or 'bankaccount'
      showCreateProjectForm: false,
      newProjectName: '',
      creatingProject: false,
      createProjectError: null,
    }
  },
  watch: {
    reservedRequestsQty: function (nVal, oVal) {
      if (!this.calculatingPrice) {
        this.getServicePriceDebounce()
      }
    },
    '$store.getters.getUser': {
      handler() {
        this.fetchCurrencyRatesIfNeeded()
      }
    }
  },
  methods: {
    fetchCurrencyRatesIfNeeded() {
      if (this.$store.getters.getCurrencyRates) return
      const user = this.$store.getters.getUser
      if (!user || !user.settings) return
      const { billing_address = {} } = user.settings
      const { country } = billing_address || {}
      const p = new Price(country, this.$store.getters.getLocale)
      const userCurrency = p.getCurrency()
      if (userCurrency !== "USD") {
        this.$store.dispatch("fetchCurrencyRates", userCurrency)
      }
    },
    formatDiscountAmount(amount) {
      const priceHelper = new Price()
      const currency = priceHelper.getCurrency()
      
      if (currency === 'USD') {
        return `$${amount}`
      } else if (currency === 'RSD') {
        return `${amount} RSD`
      }
      return `${amount} ${currency}`
    },
    
    handleBankAccountClick() {
      this.primaryPaymentMethod = 'bankaccount'
    },

    getServicePrice() {
      this.calculatingPrice = true
      this.$transactionApi.post("/transactions/calculate_price", {
        service: "reserved_requests",
        quantity: this.reservedRequestsQty
      }).then(resp => {
        this.reservedRequestsAmount = resp.data.price / 100
        this.calculatingPrice = false
      }).catch(err => {
        this.calculatingPrice = false
      })
    },

    tabChange(e) {
      window.location.hash = e
    },

    isOwner() {
      return this.model.user_uuid == this.$store.getters.getUser.uuid
    },

    createProject() {
      if (!this.newProjectName || this.newProjectName.length < 2) {
        return
      }

      this.creatingProject = true
      this.createProjectError = null

      this.$http.post('/v1/project/create', { name: this.newProjectName }).then(resp => {
        this.newProjectName = ''
        this.showCreateProjectForm = false
        this.creatingProject = false
        this.$store.commit('SET_PROJECTS', [])
        return this.$store.dispatch('fetchProjects')
      }).then(() => {
        this.$bvToast.toast(this.$t('projectCreated') || 'Project created', {
          variant: 'success',
          title: this.$t('success') || 'Success'
        })
      }).catch(error => {
        this.creatingProject = false
        if (error.response && (error.response.status === 409 || error.response.status === 400)) {
          this.createProjectError = error.response.data.message
        } else {
          this.createProjectError = this.$t('badHappened')
        }
      })
    },

    leave() {
      this.$mainDialog.msgBoxConfirm("Are you sure?").then(resp => {
        if (resp) {
          this.$http.delete("/v1/project/user/" + this.$store.getters.getUser.uuid).then(resp => {
            //console.log(resp)
            this.$router.go()
          }).catch(error => {
            if (error.response.status == 500) {
              this.$serverErrorModal(error)
              return
            }

            this.$mainDialog.msgBoxOk(err.response.data.message)
          })
        }
      })

    },

    remove() {
      this.$mainDialog.msgBoxConfirm("Are you sure you want to delete project? This action can not be reverted").then(resp => {
        if (resp) {
          const popup = this.$loadingPopup()
          this.$http.patch("/v1/project/archive").then(resp => {
            //console.log(resp)
            this.$store.commit("REMOVE_PROJECT", this.$store.getters.getCurrentProject.uuid)
            this.$router.push("/?force=true")
          }).catch(error => {
              if (error.response.status == 500) {
                  this.$serverErrorModal(error)
                  return
              }

              this.$mainDialog.msgBoxOk(err.response.data.message)
          }).finally(popup.hide)
        }
      })
    },


    getDBSize() {
      if (this.info === null) {
        return 0
      }

      return this.info.db_size
    },

    transfer() {
      this.transferModal.show = true;
    },

    triggerTransfer() {
      this.$mainDialog.msgBoxConfirm(this.$t('removedFromProject')).then(resp => {
        if (resp) {
          this.$http.post("/v1/project/transfer", { user_uuid: this.transferModal.user_uuid }).then(resp => {
            this.$router.go("/admin/dashboard")
          }).catch(err => {
            this.$mainDialog.msgBoxOk(this.$t('errorDuringTransfer') + err.response.data.message);
          })
        }
      })
    },

    saveSettings() {
      const popup = this.$loadingPopup()
      
      this.$http.post("/v1/project/settings", {
        name: this.model.name,
        primary_payment_method: this.primaryPaymentMethod
      }).then(() => {
        this.$bvToast.toast(this.$t('settings.settingsSaved'), {
          variant: 'success',
          solid: true,
        })
        // Update local settings
        if (!this.model.settings) {
          this.model.settings = {}
        }
        this.model.settings.primary_payment_method = this.primaryPaymentMethod
      }).catch(err => {
        this.$mainDialog.msgBoxOk(err.response && err.response.data ? err.response.data.message : this.$t('error'));
      }).finally(() => {
        popup.hide()
      })
    },

    reserveRequests() {
      this.reserveRequestsModal = true;
    },

    subscribed(sub) {
      this.model.account = sub.account;
    },

    async attemptUpgradeAccount(account) {
      // check is billing information filled in
      const user = this.$store.getters.getUser
      if (!user.settings.billing_address.country) {
        this.showBillingInformationModal = true
      } else {
        this.upgradeAccount(account)
      }
    },

    async upgradeAccount(account) {
      let checkout = new Checkout()
      const link = await checkout.getCheckoutLink({
        product_type: "account",
        reference: account.uuid,
        lang: this.$store.getters.getLocale,
        options: {
          variant: this.accountAnnual ? 'annual' : 'month'
        }
      })

      window.open(link.data, '_blank')
    },

    billingInfoSave() {
      this.billingInfoSaved = true
    },

    getPriceAndCurrency(price) {
      const { billing_address = {} } = this.$store.getters.getUser.settings
      const { country } = billing_address || {}

      const p = new Price(
        country,
        this.$store.getters.getLocale
      )

      return p.getPriceAndCurrency(price)
    }
  },
  computed: {
    subscriptions() {
      return this.$store.getters.getSubscriptions
    },

    model() {
      return this.$store.getters.project(localStorage.getItem("current_project_uuid"))
    },

    userOnProject() {
      return this.model.users.map(u => {
        return {
          text: u.user.name,
          value: u.user.uuid
        }
      })
    },

    cardPaymentsEnabled() {
      return Env.get('CARD_PAYMENTS_ENABLED') === true || Env.get('CARD_PAYMENTS_ENABLED') === 'true'
    },

    noPaymentMethodsAvailable() {
      return !this.cardPaymentsEnabled && !this.allowBankPayment
    },

    allowBankPayment() {
      const projectInfo = this.$store.getters.getProjectInfo
      return projectInfo && projectInfo.allow_bank_payment === true
    },

    billableRequests() {
      let billable = this.model.api_requests - (this.model.account.api_requests + this.model.reserved_api_requests);

      if (billable <= 0) {
        return 0
      }

      return billable
    },

    billableDBSize() {
      let billable = this.getDBSize() - this.model.account.database_size;

      if (billable <= 0) {
        return 0
      }

      //turn bytes into Megabytes
      return billable / 1024 / 1024;
    },

    balance() {
      if (this.info) {
        return this.info.balance / 100
      }
    },

    freeRequestsPercent() {
      let p = (this.model.api_requests / this.model.account.api_requests) * 100
      if (p > 100) return 100

      return p
    },

    billableRequestsPercent() {
      let p = ((this.model.api_requests - this.model.account.api_requests) / this.model.reserved_api_requests) * 100 - 100
      if (p < 0) return 0;

      return p
    },

    reservedRequestsPercent() {
      let p = ((this.model.api_requests - this.model.account.api_requests) / this.model.reserved_api_requests) * 100
      if (p < 0) return 0;
      if (p > 100) return 100;

      return p
    },

    databaseSize() {
      let gbSize = this.getDBSize() / 1024 / 1024 / 1024
      if (gbSize < 1024 && gbSize > 1) {
        return parseFloat(gbSize.toFixed(2))
      }

      let mbSize = this.getDBSize() / 1024 / 1024
      if (mbSize < 1024 && mbSize > 1) {
        return parseFloat(mbSize.toFixed(2))
      }

      let kbSize = this.getDBSize() / 1024
      if (kbSize < 1024 && kbSize > 1) {
        return parseFloat(kbSize.toFixed(2))
      }

      return parseFloat(this.getDBSize().toFixed(2))
    },

    databaseSizePercent() {
      let p = (this.getDBSize() / this.model.account.database_size) * 100
      if (p > 100) return 100

      return parseInt(p.toFixed(2))
    },

    billableDBSizePercent() {
      let p = (this.getDBSize() / this.model.account.database_size) * 100 - 100
      if (p < 0) return 0;

      return p.toFixed(2)
    },

    databaseSizeSuffix() {
      let size = this.getDBSize();

      let gbSize = size / 1024 / 1024 / 1024
      if (gbSize < 1024 && gbSize > 1) {
        return "GB"
      }

      let mbSize = size / 1024 / 1024
      if (mbSize < 1024 && mbSize > 1) {
        return "MB"
      }

      let kbSize = size / 1024
      if (kbSize < 1024 && kbSize > 1) {
        return "KB"
      }

      return "B"
    },

    minReservedRequests() {
      return 10000
    },

    availableUpgrades() {
      const subscriptions = this.subscriptions || []
      let subAccount = subscriptions.find(s => {
        return s.license_product.group == "Account"
      })

      if (subAccount && subAccount.payment_status == "pending") {
        return []
      }

      const rates = this.$store.getters.getCurrencyRates || {}

      return this.accounts.filter(a => {
        return a.level > this.model.account.level
      }).map(a => {
        return new Account(a, rates)
      }).sort((a, b) => {
        return b.level - a.level
      })
    },

    currency() {
      try {
        const p = new Price(
          null,
          this.$store.getters.getLocale
        )

        return p.getCurrency()
      } catch {
        const p = new Price(
          null,
          this.$store.getters.getLocale
        )

        return p.getCurrency()
      }
    }
  },
  created() {
    if (this.model === undefined) {
      this.$router.push({ path: "/admin/dashboard" })
    }

    this.fetchCurrencyRatesIfNeeded()

    this.$store.dispatch("fetchProjectInfo").then(data => {
      this.info = data
      
      // Initialize payment method from fresh API response (primary source),
      // falling back to model settings for backward compatibility.
      if (data && data.primary_payment_method) {
        this.primaryPaymentMethod = data.primary_payment_method
        // Sync the fresh value back to the model so save works correctly
        if (this.model && this.model.settings) {
          this.model.settings.primary_payment_method = data.primary_payment_method
        }
      } else if (this.model && this.model.settings && this.model.settings.primary_payment_method) {
        this.primaryPaymentMethod = this.model.settings.primary_payment_method
      }
      // Auto-correct if the saved method is no longer available
      const cardEnabled = Env.get('CARD_PAYMENTS_ENABLED') === true || Env.get('CARD_PAYMENTS_ENABLED') === 'true'
      const bankAllowed = data && data.allow_bank_payment === true
      if (this.primaryPaymentMethod === 'card' && !cardEnabled && bankAllowed) {
        this.primaryPaymentMethod = 'bankaccount'
      } else if (this.primaryPaymentMethod === 'bankaccount' && !bankAllowed && cardEnabled) {
        this.primaryPaymentMethod = 'card'
      }
    }).catch(error => {
      if (error.response !== undefined) {
        if (error.response.status == 500) {
          this.$serverErrorModal(error)
          return
        }
      }
    })

    let hash = window.location.hash
    this.tabIndex = [0, this.tabHash.indexOf(hash), 0]

    var subtabMap = { general: 0, subscriptions: 1, invoices: 2, 'payment-methods': 3 }
    var subtab = this.$route.query.subtab
    if (subtab && subtabMap[subtab] !== undefined) {
      this.tabIndex[2] = subtabMap[subtab]
    }

    if (this.$route.query.paymentCanceled !== undefined) {
      this.$mainDialog.msgBoxOk(this.$t('yourPaymentFailed'))
    }

    if (this.$route.query.paymentSuccess !== undefined) {
      this.$mainDialog.msgBoxOk(this.$t('paymentSuccessful'));
    }

    this.$transactions.prices(this.currency).then(resp => {
      this.prices = resp.data
    }).catch(err => {
      console.error(err)
      this.$bvToast.toast(this.$t('unexpectedError'), {
        variant: 'danger',
        title: this.$t('error')
      })
    })

    this.$http.get("/v1/user/register/accounts").then(resp => {
      this.accounts = resp.data
    }).catch(err => {
      console.error(err)
      this.$bvToast.toast(this.$t('unexpectedError'), {
        variant: 'danger',
        title: this.$t('error')
      })

      console.error(err)
    })

    this.$store.dispatch("fetchSubscriptions")
  },

  mounted() {
    if (!window.kortex) return

    window.kortex.showHelper()
    window.kortex.registerContext(
      'Project Settings',
      'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
      'The user is managing their project configuration. You can read the current settings (name, UUID, payment method, ' +
      'database size, API usage), update the project name, switch between card and bank payment, save settings, ' +
      'or create a new project. Use get_project_settings to see everything first, then suggest changes.',
      'icon-screen-desktop'
    )

    var self = this

    // ── Read project state ──────────────────────────────────────
    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_project_settings',
          description: 'Returns the current project settings: name, UUID, payment method, database size, API request usage, and available payment options.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        var model = self.model
        if (!model) return { error: 'Project not loaded yet' }
        return {
          uuid: model.uuid,
          name: model.name,
          created_at: model.CreatedAt,
          is_owner: self.isOwner(),
          is_admin: self.$isAdmin ? self.$isAdmin() : false,
          payment_method: self.primaryPaymentMethod,
          available_payment_methods: {
            card: self.cardPaymentsEnabled,
            bank_account: self.allowBankPayment
          },
          database: {
            size_bytes: self.getDBSize(),
            size_formatted: String(self.databaseSize) + ' ' + self.databaseSizeSuffix,
            limit_bytes: model.account ? model.account.database_size : 0
          },
          api_requests: {
            used: model.api_requests,
            included: model.account ? model.account.api_requests : 0,
            reserved: model.reserved_api_requests || 0,
            billable: self.billableRequests
          },
          account: model.account ? {
            name: model.account.name,
            level: model.account.level,
            pay_as_you_go: model.account.pay_as_you_go || false
          } : null
        }
      }
    )

    // ── Update project name ─────────────────────────────────────
    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_project_name',
          description: 'Updates the project name in the form. The user must still click Save to persist the change.',
          parameters: {
            type: 'object',
            required: ['name'],
            properties: {
              name: { type: 'string', description: 'The new project name (min 2 characters).' }
            }
          }
        }
      },
      function (args) {
        var name = (args.name || '').trim()
        if (name.length < 2) return { error: 'Project name must be at least 2 characters.' }
        if (!self.$isAdmin || !self.$isAdmin()) return { error: 'Only admins can change the project name.' }
        self.$set(self.model, 'name', name)
        return { name: name, saved: false, note: 'Name updated in form. User must click Save to persist.' }
      }
    )

    // ── Set payment method ──────────────────────────────────────
    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'set_payment_method',
          description: 'Switches the primary payment method between card and bank account. The user must still click Save to persist.',
          parameters: {
            type: 'object',
            required: ['method'],
            properties: {
              method: { type: 'string', enum: ['card', 'bankaccount'], description: 'Payment method: "card" or "bankaccount".' }
            }
          }
        }
      },
      function (args) {
        var method = args.method
        if (method !== 'card' && method !== 'bankaccount') {
          return { error: 'Method must be "card" or "bankaccount".' }
        }
        if (method === 'card' && !self.cardPaymentsEnabled) {
          return { error: 'Card payments are not enabled for this project.' }
        }
        if (method === 'bankaccount' && !self.allowBankPayment) {
          return { error: 'Bank account payments are not available for this project.' }
        }
        self.primaryPaymentMethod = method
        return { payment_method: method, saved: false, note: 'Payment method updated in form. User must click Save to persist.' }
      }
    )

    // ── Save settings ───────────────────────────────────────────
    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'save_project_settings',
          description: 'Persists the current project name and payment method settings to the server.',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        if (!self.$isAdmin || !self.$isAdmin()) return { error: 'Only admins can save project settings.' }
        self.saveSettings()
        return { saved: true, note: 'Save request sent. The toast notification will confirm success.' }
      }
    )

    // ── Create new project ──────────────────────────────────────
    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'create_project',
          description: 'Creates a new Protokol project with the given name and navigates to it.',
          parameters: {
            type: 'object',
            required: ['name'],
            properties: {
              name: { type: 'string', description: 'Name for the new project (min 2 characters).' }
            }
          }
        }
      },
      function (args) {
        var name = (args.name || '').trim()
        if (name.length < 2) return { error: 'Project name must be at least 2 characters.' }
        if (!self.isOwner()) return { error: 'Only the project owner can create new projects.' }
        return self.$http.post('/v1/project/create', { name: name }).then(function (resp) {
          self.$store.commit('SET_PROJECTS', [])
          return self.$store.dispatch('fetchProjects').then(function () {
            self.$bvToast.toast(self.$t('projectCreated') || 'Project created', {
              variant: 'success',
              title: self.$t('success') || 'Success'
            })
            return { created: true, name: name, note: 'Project created successfully.' }
          })
        }).catch(function (error) {
          var msg = 'Failed to create project.'
          if (error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message
          }
          return { error: msg }
        })
      }
    )
  },

  beforeDestroy() {
    if (!window.kortex) return
    window.kortex.unregisterContext()
    window.kortex.hideHelper()
    window.kortex.unregisterTool('get_project_settings')
    window.kortex.unregisterTool('set_project_name')
    window.kortex.unregisterTool('set_payment_method')
    window.kortex.unregisterTool('save_project_settings')
    window.kortex.unregisterTool('create_project')
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.settings-section {
  margin-bottom: 40px;
  padding-bottom: 32px;
  border-bottom: 1px solid $dark-border2;

  &:last-child {
    border-bottom: none;
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .section-description {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin-bottom: 20px;
  }
}

.danger-zone {
  background: rgba(220, 38, 38, 0.05);
  padding: 24px;
  border-radius: 8px;
  border: 1px solid rgba(220, 38, 38, 0.2);
}

.payment-method-options {
  display: grid;
  gap: 12px;
}

.payment-method-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: $dark-hard2-background;
  border: 2px solid $dark-border2;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--text-muted);
  }

  &.selected {
    border-color: $protokol-royal-active;
    background: rgba(102, 126, 234, 0.1);
  }

  &.disabled {
    opacity: 0.6;
    cursor: pointer;
    
    &:hover {
      border-color: rgba(255, 193, 7, 0.5);
    }

    .option-icon {
      background: rgba(255, 193, 7, 0.2);
      color: #ffc107;
    }
  }

  .option-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--border-default);
    border-radius: 8px;
    color: var(--text-primary);
  }

  .option-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .option-title {
      font-weight: 600;
      color: var(--text-primary);
      font-size: 1rem;
    }

    .option-description {
      color: var(--text-secondary);
      font-size: 0.85rem;
    }
  }

  .option-check {
    color: $protokol-royal-active;
    font-size: 1.25rem;
  }

  .option-lock {
    color: #ffc107;
    font-size: 1.25rem;
  }
}
</style>
